import { useState } from 'react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { ConfirmationRequest } from '@/hooks/use-agent-chat.hook';
import { emailPlaceholder, type EmailEdits } from './one-off-send.util';

export type { EmailEdits };

/** Clu's one email from the user's own mailbox, confirmed on its card. */
export const SEND_MAILBOX_EMAIL_ACTION = 'send_mailbox_email';

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * The email card's subject and email as the user edits them, prefilled from
 * the card. `edits` is null for any other card. `invalid` holds Confirm back
 * while the subject is blank or more than one line, the email is blank, or
 * either holds a placeholder the server would refuse; the server checks the
 * edited text again on the send.
 */
export function useEmailEdits(request: ConfirmationRequest) {
  const isEmail = request.tool_name === SEND_MAILBOX_EMAIL_ACTION;
  const [edits, setEdits] = useState<EmailEdits>(() => ({
    subject: text(request.arguments?.subject),
    body: text(request.arguments?.body),
  }));
  const invalid =
    isEmail &&
    (!edits.subject.trim() ||
      /[\r\n]/.test(edits.subject) ||
      !edits.body.trim() ||
      emailPlaceholder(edits) !== null);
  return { edits: isEmail ? edits : null, setEdits, invalid };
}

/**
 * The email a send card asks to send: From and To as the server stored
 * them, then the Subject and the email, which the user can edit. Null for
 * any other card.
 */
export function SendEmailPreview({
  request,
  edits,
  onEdit,
  disabled = false,
}: {
  request: ConfirmationRequest;
  edits: EmailEdits | null;
  onEdit: (edits: EmailEdits) => void;
  disabled?: boolean;
}) {
  if (request.tool_name !== SEND_MAILBOX_EMAIL_ACTION || !edits) return null;
  const args = request.arguments ?? {};
  const placeholder = emailPlaceholder(edits);
  const rows: [string, string][] = [
    ['From', text(args.from_email)],
    ['To', text(args.to_email)],
  ];
  return (
    <div
      className='overflow-hidden rounded-[8px] border border-border bg-background text-[12px] leading-[16px]'
      data-testid='send-email-preview'
    >
      <dl className='grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-[12px] gap-y-[4px] border-b border-border px-[10px] py-[8px]'>
        {rows.map(([label, value]) => (
          <div key={label} className='contents'>
            <dt className='text-muted-foreground'>{label}</dt>
            <dd className='min-w-0 font-medium break-words text-foreground'>
              {value}
            </dd>
          </div>
        ))}
        <dt className='text-muted-foreground'>Subject</dt>
        <dd className='min-w-0'>
          <Input
            aria-label='Subject'
            value={edits.subject}
            maxLength={200}
            disabled={disabled}
            onChange={(event) =>
              onEdit({ ...edits, subject: event.target.value })
            }
            className='h-[28px] rounded-[6px] px-[8px] text-[12px] font-medium md:text-[12px]'
          />
        </dd>
      </dl>
      <div className='px-[10px] py-[8px]'>
        <Textarea
          aria-label='Email'
          value={edits.body}
          maxLength={20_000}
          rows={8}
          disabled={disabled}
          onChange={(event) => onEdit({ ...edits, body: event.target.value })}
          className='max-h-[240px] rounded-[6px] px-[8px] py-[6px] text-[12px] leading-[16px] md:text-[12px]'
        />
        {placeholder && (
          <p role='alert' className='mt-[6px] text-[12px] text-destructive'>
            Write out the real words instead of placeholders like {placeholder}.
          </p>
        )}
      </div>
    </div>
  );
}
